import { useMemo, useState } from "react";
import * as Animate from "react-reveal";
import Avatar from "../../../assets/images/png/utiva.png";
import StoryModal from "../../../components/modals/StoryModal";
import {
  getStoryCategory,
  storyCategories,
} from "../../../utils/storyCategories";
import { getMarkerWord } from "../../../utils/storyMarker";

const markerThemes = [
  { color: "#050794", background: "#e9edff" },
  { color: "#744c00", background: "#fff0c7" },
  { color: "#5a31aa", background: "#f1eaff" },
  { color: "#116447", background: "#e3f7ef" },
];

const StoryCard = ({ story, index, onOpen }) => {
  const theme = markerThemes[index % markerThemes.length];
  const category = getStoryCategory(story?.category);
  const image =
    story?.imageUrl && !story.imageUrl.includes("cloudinary")
      ? story.imageUrl
      : Avatar;

  return (
    <article
      onClick={onOpen}
      className="border border-[#dbe3f4] bg-white rounded-[19px] flex flex-col cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:border-[#bbc8e3]"
      style={{
        minHeight: "315px",
        padding: "22px",
        boxShadow: "0 12px 30px rgba(28,35,70,0.06)",
      }}
    >
      <div className="grid stories-grid-person items-center gap-[13px]">
        <img
          src={image}
          alt={story?.name}
          loading="lazy"
          className="rounded-full object-cover"
          style={{ width: "58px", height: "58px", border: "3px solid #edf1fb" }}
        />
        <div>
          <h3 className="text-primary font-bold text-[18px] leading-[1.3] m-0">
            {story?.name}
          </h3>
          <p className="text-[#5a6275] text-[15px] leading-[1.35] mt-1 mb-0">
            {story?.companyRole}
          </p>
        </div>
        <span
          className="rounded-lg text-[15px] font-extrabold tracking-[0.04em] whitespace-nowrap"
          style={{
            color: theme.color,
            background: theme.background,
            padding: "7px 8px",
          }}
        >
          {getMarkerWord(story?.companyRole)}
        </span>
      </div>

      <p
        className="text-[#202332] text-[15px] leading-[1.7]"
        style={{ margin: "23px 0" }}
      >
        {story?.experience?.length > 160
          ? `${story.experience.slice(0, 160)}…`
          : story?.experience}
      </p>

      <footer
        className="flex justify-between items-end gap-[15px] border-t border-[#e8edf7]"
        style={{ marginTop: "auto", paddingTop: "15px" }}
      >
        {category && (
          <span className="text-[#8c5b00] tracking-[0.05em] uppercase text-[15px] font-extrabold">
            {category.label}
          </span>
        )}
        <b
          aria-hidden="true"
          className="text-secondary"
          style={{
            height: "28px",
            fontFamily: "Georgia, serif",
            fontSize: "43px",
            lineHeight: 1,
          }}
        >
          “
        </b>
      </footer>
    </article>
  );
};

const StoriesListing = ({ stories, loading }) => {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedStory, setSelectedStory] = useState(null);

  const filteredStories = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (stories || []).filter((story) => {
      const matchesCategory =
        activeCategory === "all" || story?.category === activeCategory;
      const matchesQuery =
        !query ||
        story?.name?.toLowerCase().includes(query) ||
        story?.companyRole?.toLowerCase().includes(query);
      return matchesCategory && matchesQuery;
    });
  }, [stories, search, activeCategory]);

  const hasFilters = search.trim() !== "" || activeCategory !== "all";

  return (
    <section
      id="story-library"
      aria-labelledby="story-library-title"
      className="mx-auto px-6 lg:px-[26px] pt-12 lg:pt-[66px] pb-16 lg:pb-[88px]"
      style={{ maxWidth: "1280px", scrollMarginTop: "96px" }}
    >
      <div className="grid stories-grid-library-header items-end gap-8 lg:gap-[60px]">
        <div>
          <p className="text-[#8c5b00] tracking-[0.1em] uppercase text-[15px] font-extrabold mb-2">
            Voices From The Community
          </p>
          <h2
            className="text-primary tracking-[-0.048em] max-w-[700px] m-0"
            style={{ fontSize: "clamp(34px, 4vw, 51px)", lineHeight: 1.12 }}
          >
            Go straight to the story that matters to you.
          </h2>
        </div>
        <label className="grid gap-2">
          <span className="text-primary text-[15px] font-extrabold">
            Search Talent Stories
          </span>
          <input
            type="search"
            placeholder="Search by name, role or skill"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full text-[#202332] bg-white rounded-xl px-4 py-[13px] text-[16px] outline-none focus:border-primary"
            style={{ minHeight: "54px", border: "2px solid #ced8ed" }}
          />
        </label>
      </div>

      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-[18px] rounded-2xl mt-7 mb-6"
        style={{
          border: "1px solid #dbe3f4",
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          padding: "13px",
          // position: "sticky",
          // top: "calc(var(--header-height, 90px) + var(--subnav-height, 68px))",
          zIndex: 12,
          boxShadow: "0 13px 34px rgba(25,34,78,0.08)",
        }}
      >
        <div
          className="flex gap-[7px] overflow-x-auto no-scrollbar min-w-0 w-full sm:w-auto"
          role="group"
          aria-label="Filter stories by learning area"
        >
          <button
            type="button"
            onClick={() => setActiveCategory("all")}
            aria-pressed={activeCategory === "all"}
            className={`flex-none rounded-full text-[15px] font-bold px-[14px] whitespace-nowrap transition-colors ${
              activeCategory === "all"
                ? "bg-primary border-primary text-white"
                : "bg-[#f8faff] border-[#d3dced] text-[#50586b] hover:border-primary hover:text-primary"
            }`}
            style={{ border: "1px solid", minHeight: "42px" }}
          >
            All stories
          </button>
          {storyCategories.map((category) => (
            <button
              key={category.value}
              type="button"
              onClick={() => setActiveCategory(category.value)}
              aria-pressed={activeCategory === category.value}
              className={`flex-none rounded-full text-[15px] font-bold px-[14px] whitespace-nowrap transition-colors ${
                activeCategory === category.value
                  ? "bg-primary border-primary text-white"
                  : "bg-[#f8faff] border-[#d3dced] text-[#50586b] hover:border-primary hover:text-primary"
              }`}
              style={{ border: "1px solid", minHeight: "42px" }}
            >
              {category.label}
            </button>
          ))}
        </div>
        <span
          className="flex-none rounded-full text-[15px] font-extrabold"
          style={{
            color: "#744c00",
            background: "#fff2ce",
            padding: "7px 11px",
          }}
        >
          {filteredStories.length} stories
        </span>
      </div>

      {loading ? (
        <p className="text-center py-16 text-[#5a6275]">Loading stories…</p>
      ) : filteredStories.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[17px]">
          {filteredStories.map((story, index) => (
            <Animate.Fade bottom key={story._id || index}>
              <StoryCard
                story={story}
                index={index}
                onOpen={() => setSelectedStory(story)}
              />
            </Animate.Fade>
          ))}
        </div>
      ) : (
        <div
          className="text-center grid place-items-center rounded-[20px]"
          style={{
            background: "#f7f9ff",
            border: "1px dashed #bdc8e0",
            minHeight: "340px",
            padding: "45px 24px",
          }}
        >
          <span
            className="text-secondary block"
            style={{ fontSize: "46px", transform: "rotate(-15deg)" }}
          >
            ⌕
          </span>
          <h3 className="text-primary text-[27px]" style={{ margin: "7px 0" }}>
            {stories?.length === 0
              ? "No stories published yet"
              : "No stories match your search"}
          </h3>
          <p className="text-[#5a6275] text-[15px] leading-[1.65] max-w-[580px] mb-[18px]">
            {stories?.length === 0
              ? "Be the first to share what Utiva changed for you."
              : "Try a different learning area or search term."}
          </p>
          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setActiveCategory("all");
              }}
              className="bg-primary text-white rounded-[10px] text-[15px] font-extrabold px-[17px]"
              style={{ minHeight: "46px" }}
            >
              Reset filters
            </button>
          )}
        </div>
      )}

      <p className="text-[#70788a] text-[15px] leading-[1.6] mt-[22px] mb-0">
        Stories are shared by Utiva learners and reviewed before publishing.
      </p>

      {selectedStory && (
        <StoryModal
          show={!!selectedStory}
          handleModalClose={() => setSelectedStory(null)}
          selectedTestimony={selectedStory}
        />
      )}
    </section>
  );
};

export default StoriesListing;
